import type { PropsWithChildren } from 'react'
import { createContext, useContext, useEffect, useState } from 'react'

import { useUsername } from '@jotta/grpc-connect-client/customer'
import { usePhotoSubscription } from '@jotta/grpc-connect-client/useSubscription'
import { AppError } from '@jotta/types/AppError'
import { useBrandStore } from '@jotta/ui/useBrandTheme'

import type { AlbumInfoQueryStore } from './AlbumInfoQueryStore'
import { PhotoStore } from './PhotoStore'
import { useSelectionStore as useSelectionStoreReal } from './SelectionStore'

interface PhotoContextValue {
  photoStore: PhotoStore
}

export const PhotoContext = createContext<PhotoContextValue | undefined>(
  undefined,
)

function useCreatePhotoStore(photoStore?: PhotoStore) {
  const brandStore = useBrandStore()
  const username = useUsername()
  const [store] = useState(
    () => photoStore || new PhotoStore(brandStore, username),
  )

  useEffect(() => {
    store.username = username
  }, [username, store])

  return store
}

export function PhotoContextProvider({
  children,
  photoStore,
}: PropsWithChildren<Partial<PhotoContextValue>>) {
  const store = useCreatePhotoStore(photoStore)

  return (
    <PhotoContext.Provider
      value={{
        photoStore: store,
      }}
    >
      {children}
    </PhotoContext.Provider>
  )
}

export function useAlbumInvalidation(albumInfoQueryStore: AlbumInfoQueryStore) {
  usePhotoSubscription(ref => {
    switch (ref.case) {
      case 'photoAlbum': {
        const albumId = ref.value.id
        if (albumInfoQueryStore.id === albumId) {
          albumInfoQueryStore.refetch()
        }
      }
    }
  })
}

export function usePhotoStore() {
  const photoContext = useContext(PhotoContext)
  if (!photoContext) {
    throw new AppError({
      message: 'usePhotoStore must be used inside PhotoContextProvider',
    })
  }
  return photoContext.photoStore
}
export function usePhotoUploadStore() {
  const store = usePhotoStore()
  return store.upload
}

export const useSelectionStore = useSelectionStoreReal
export function useMediaObjectStore() {
  const store = usePhotoStore()
  return store.mediaObjects
}
export function useSearchStore() {
  const store = usePhotoStore()
  return store.search
}
